import React, { useEffect, useState, useCallback } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import Column from './Column';
import { Card, CardBody } from 'reactstrap';
import { AxiosAPI } from '../../../../../../../helpers/AxiosAPI';
import { API_WorkOrders } from '../../../../../../../constants/APIs';
import { useSelector } from 'react-redux';
import { NotificationManager } from '../../../../../../../components/common/react-notifications';

// Available statuses: Submitted, Acknowledged, Completed, Archived, Cancelled
const ALLOWED_MOVES = {
  submitted: ['acknowledged', 'completed', 'cancelled', 'archived'],
  acknowledged: ['completed', 'cancelled', 'archived'],
  completed: ['archived'],
  archived: [],
  cancelled: ['archived'],
};

const columnOrder = [
  { id: 'submitted', title: 'Submitted' },
  { id: 'acknowledged', title: 'Acknowledged' },
  { id: 'completed', title: 'Completed' },
  { id: 'archived', title: 'Archived' },
  { id: 'cancelled', title: 'Cancelled' },
];

const WorkOrdersKanbanBoard = ({
  data = [],
  loading = false,
  onRefresh = () => { },
}) => {
  const { userName } = useSelector(({ settings }) => ({
    userName: settings.userName,
  }));

  const buildColumns = useCallback((rows) => {
    const base = columnOrder.reduce((acc, column) => {
      acc[column.id] = [];
      return acc;
    }, {});

    rows.forEach((row, index) => {
      if (!row) return;
      const statusKey = (row.status || 'submitted').toLowerCase();

      // Fallback if status isn't matched exactly
      const columnId = base[statusKey] ? statusKey : 'submitted';

      base[columnId].push({
        id: row.id ? String(row.id) : `wo-${index}`,
        row,
        isDragDisabled: row.system === 'Flexkeeping',
      });
    });

    return base;
  }, []);

  const [columns, setColumns] = useState(() =>
    buildColumns(Array.isArray(data) ? data : [])
  );

  useEffect(() => {
    setColumns(buildColumns(Array.isArray(data) ? data : []));
  }, [data, buildColumns]);

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const moved = columns[source.droppableId][source.index];

    if (moved?.row?.system === 'Flexkeeping') {
      NotificationManager.error(
        'You cannot move this work order as it is synced from Flexkeeping.',
        'Permission Denied',
        3000
      );
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const startColumnId = source.droppableId;
    const endColumnId = destination.droppableId;

    const allowedTargets = ALLOWED_MOVES[startColumnId] || [];
    if (!allowedTargets.includes(endColumnId)) {
      NotificationManager.error(
        `A ${startColumnId} work order cannot be moved to ${endColumnId}.`,
        'Action not allowed',
        3000
      );
      return;
    }

    const startTasks = Array.from(columns[startColumnId]);
    const [movedTask] = startTasks.splice(source.index, 1);

    const endTasks = Array.from(columns[endColumnId]);
    endTasks.splice(destination.index, 0, movedTask);

    // Optimistically update UI
    setColumns((prev) => ({
      ...prev,
      [startColumnId]: startTasks,
      [endColumnId]: endTasks,
    }));

    // Find title matching the column id
    const newStatusTitle =
      columnOrder.find((c) => c.id === endColumnId)?.title || 'Submitted';

    try {
      await AxiosAPI({
        method: 'put',
        url: `${API_WorkOrders}/${movedTask.row.id}`,
        data: {
          ...movedTask.row,
          status: newStatusTitle,
          modifiedBy: userName || 'system',
        },
      });
      onRefresh();
    } catch (err) {
      console.error('Status update failed', err);
      NotificationManager.error(
        'Failed to update work order status',
        'Error',
        3000
      );
      // Revert on failure by refreshing
      onRefresh();
    }
  };

  if (loading) {
    return (
      <Card className="d-flex flex-row mb-3">
        <CardBody>
          <h4 style={{ textAlign: 'center' }}>Loading...</h4>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="work-order-kanban-wrapper">
      <style>{`
                .work-order-kanban-wrapper {
                    display:flex;
                    margin-top:20px;
                    justify-content:flex-start;
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #e7e9ef;
                    overflow-x: auto;
                }
                .work-order-kanban-columns {
                    display: flex;
                    gap: 16px;
                    min-width: min-content;
                }
                .work-order-kanban-column {
                    background: #fdfdfd;
                    border: 1px solid #d3d8e0;
                    border-radius: 16px;
                    padding: 16px;
                    width: 310px;
                    min-height: 520px;
                    box-shadow: 0 4px 12px rgba(20, 43, 63, 0.05);
                }
                .work-order-kanban-column.is-dragging-over {
                    border-color: var(--theme-color-1);
                    background: #f4f8fb;
                }
                .work-order-kanban-column__title {
                    font-size: 16px;
                    font-weight: 600;
                    text-align:center;
                    color: #143652;
                    margin-bottom: 14px;
                }
                .work-order-kanban-card {
                    position: relative;
                    background: #ffffff;
                    border-radius: 14px;
                    padding: 14px 16px;
                    border: 1px solid #c9d2dd;
                    margin-bottom: 12px;
                    transition: box-shadow 0.2s ease, transform 0.2s ease;
                }
                .work-order-kanban-card.is-dragging {
                    transform: rotate(2deg);
                    box-shadow: 0 12px 24px rgba(20, 43, 63, 0.15);
                    z-index: 9999;
                }
                .work-order-kanban-card__guest {
                    font-weight: 600;
                    color: #12263f;
                    margin-bottom: 4px;
                }
                .work-order-kanban-card__date {
                    font-size: 13px;
                    color: #516074;
                    line-height: 1.3;
                    margin-top: 4px;
                }         
                .work-order-kanban-card__actions {
                    display:flex;
                    gap:8px;
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }
                .work-order-kanban-card:hover .work-order-kanban-card__actions {
                    opacity: 1;
                }
                .work-order-kanban-card__edit-btn {
                    background: rgba(255,255,255,0.95);
                    border: 1px solid #d3d8e0;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    cursor: pointer;
                    color: #143652;
                    transition: background 0.2s ease, color 0.2s ease;
                }
                .work-order-kanban-card__edit-btn:hover {
                    background: var(--theme-color-1);
                    color: #ffffff;
                }
                .work-order-kanban-column__empty {
                    border: 2px dashed #d3d8e0;
                    border-radius: 12px;
                    padding: 30px 20px;
                    text-align: center;
                    font-size: 14px;
                    color: #8a94a6;
                    background: transparent;
                }
                .wo-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    font-size: 11px;
                    font-weight: 600;
                    border-radius: 12px;
                    margin-bottom: 8px;
                }
                .wo-badge-priority-1 { background: #e0f2fe; color: #0284c7; }
                .wo-badge-priority-2 { background: #dcfce7; color: #16a34a; }
                .wo-badge-priority-3 { background: #fef08a; color: #ca8a04; }
                .wo-badge-priority-4 { background: #fee2e2; color: #dc2626; }
            `}</style>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="work-order-kanban-columns">
          {columnOrder.map(({ id, title }) => (
            <Column
              key={id}
              columnId={id}
              title={title}
              tasks={columns[id] || []}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

export default WorkOrdersKanbanBoard;
